import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMessage } from "@/lib/api";
import { PageTitle, Loading, ErrorBox, Empty } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { selectCls } from "@/pages/Take5New";
import { identLabel } from "@/lib/labels";

const GROUPS = [[0, "Attribués à moi"], [1, "Attribués à mon équipe"], [2, "Workplace"], [3, "Attribués à d'autres"]];

const Row = ({ e }) => {
  const navigate = useNavigate();
  return (
    <tr data-testid={`equipment-row-${e.code}`} onClick={() => navigate(`/equipements/${e.id}`)} className="cursor-pointer border-b border-border transition-colors hover:bg-amber-500/5">
      <td className="px-3 py-3">
        <Link to={`/equipements/${e.id}`} data-testid={`equipment-card-${e.code}`} className="font-data text-sm font-semibold text-amber-500">{e.code}</Link>
        <p className="text-sm font-semibold md:hidden">{e.name}</p>
      </td>
      <td className="hidden px-3 py-3 text-sm font-semibold md:table-cell">{e.name}</td>
      <td className="hidden px-3 py-3 text-sm text-muted-foreground lg:table-cell">{e.type_name}</td>
      <td data-testid={`equipment-identifiers-${e.code}`} className="hidden px-3 py-3 xl:table-cell">
        <div className="flex flex-wrap gap-1">{(e.identifiers || []).map((i) => <span key={i.id} className="rounded border border-border px-1.5 py-0.5 font-data text-[11px]">{identLabel(i)} : {i.value}</span>)}</div>
      </td>
      <td className="hidden px-3 py-3 text-sm lg:table-cell">{e.site?.name}</td>
      <td data-testid={`equipment-allocation-${e.code}`} className="hidden px-3 py-3 text-sm sm:table-cell">{e.allocation?.label}</td>
      <td className="px-3 py-3 text-right"><StatusBadge status={e.status} testId={`equipment-status-${e.code}`} /></td>
    </tr>
  );
};

const EquipmentTable = ({ items }) => (
  <div className="overflow-hidden rounded-xl border border-border bg-card">
    <table data-testid="equipment-table" className="w-full text-left">
      <thead className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
        <tr>
          <th className="px-3 py-2">Code</th><th className="hidden px-3 py-2 md:table-cell">Nom</th>
          <th className="hidden px-3 py-2 lg:table-cell">Type</th><th className="hidden px-3 py-2 xl:table-cell">Identifiants</th>
          <th className="hidden px-3 py-2 lg:table-cell">Site</th><th className="hidden px-3 py-2 sm:table-cell">Attribution</th>
          <th className="px-3 py-2 text-right">État</th>
        </tr>
      </thead>
      {GROUPS.map(([p, label]) => {
        const rows = items.filter((e) => (e.allocation?.priority ?? 2) === p);
        if (!rows.length) return null;
        return (
          <tbody key={p} data-testid={`equipment-group-${p}`}>
            <tr><td colSpan={7} className="bg-background/60 px-3 py-2 text-xs font-bold uppercase tracking-wide text-amber-500">{label} ({rows.length})</td></tr>
            {rows.map((e) => <Row key={e.id} e={e} />)}
          </tbody>
        );
      })}
    </table>
  </div>
);

export default function EquipmentList() {
  const [items, setItems] = useState(null);
  const [sites, setSites] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [siteId, setSiteId] = useState(params.get("site") || "");
  const category = params.get("categorie") || "";
  const setCategory = (v) => setParams(v ? { categorie: v } : {}, { replace: true });
  const [error, setError] = useState("");

  useEffect(() => { api.get("/referentials").then((r) => setSites(r.data.sites)).catch(() => {}); }, []);
  const load = () => {
    setError("");
    api.get("/equipment", { params: { q, site_id: siteId, category } }).then((r) => setItems(r.data)).catch((e) => setError(errMessage(e)));
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, siteId, category]);

  return (
    <div>
      <PageTitle kicker="Équipements et véhicules" title={category === "vehicule" ? "Contrôler un véhicule" : category === "equipement" ? "Contrôler un équipement" : "Équipements et véhicules"} testId="equipment-title" />
      <div className="mb-3 flex flex-wrap gap-2" role="tablist">
        {[["", "Tout"], ["vehicule", "Véhicules"], ["equipement", "Équipements"]].map(([v, l]) => (
          <button key={v || "all"} type="button" role="tab" aria-selected={category === v} data-testid={`equipment-category-${v || "all"}`} onClick={() => setCategory(v)}
            className={`h-10 rounded-full border px-4 text-sm font-semibold transition-colors ${category === v ? "border-amber-500 bg-amber-500 text-slate-950" : "border-border hover:border-amber-500/60"}`}>{l}</button>
        ))}
      </div>
      <div className="mb-5 grid gap-3 sm:grid-cols-[1fr_260px]">
        <div className="relative">
          <Search className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
          <Input data-testid="equipment-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code, immatriculation, n° de parc, n° de série, alias, nom, type…" className="h-12 pl-10 text-base" />
        </div>
        <select data-testid="equipment-site-filter" className={selectCls} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">Tous les sites</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      {error && <ErrorBox message={error} onRetry={load} />}
      {!items && !error && <Loading />}
      {items && !items.length && <Empty testId="equipment-empty">Aucun équipement ne correspond à la recherche.</Empty>}
      {items?.length > 0 && <EquipmentTable items={items} />}
    </div>
  );
}
